import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { FileText, Download, Mail, FileDown, X } from "lucide-react";
import { downloadCsv } from "@/lib/csv";
import { downloadPdf, emailPdf } from "@/lib/pdf";
import { toast } from "sonner";

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("");
  const [emailModal, setEmailModal] = useState(null); // { invoiceId, defaultTo }
  const [recapModal, setRecapModal] = useState(false);
  const [batchModal, setBatchModal] = useState(false);
  const [stores, setStores] = useState([]);

  useEffect(() => {
    api.get(`/admin/invoices${kind ? `?kind=${kind}` : ""}`).then((r) => setItems(r.data));
  }, [kind]);

  useEffect(() => {
    // Preload merchant stores for the monthly-recap picker
    api.get("/admin/subscriptions?page=1&page_size=100").then((r) => {
      setStores(r.data.items || []);
    }).catch(() => {});
  }, []);

  const defaultMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  return (
    <div data-testid="admin-invoices">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="pf-chip pf-chip-cyan"><FileText className="w-3 h-3" /> Finance</div>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Invoices</h2>
          <p className="text-sm text-slate-600 mt-1">Every SaaS subscription charge + shopper wallet payout across the platform. SOC-2 friendly PDF statements available on every row.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setBatchModal(true)} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="recap-batch-open" title="Email the monthly recap PDF to every merchant (super_admin only)">
            <Mail className="w-3 h-3" /> Send all recaps
          </button>
          <button onClick={() => setRecapModal(true)} className="pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="monthly-recap-open">
            <FileDown className="w-3 h-3" /> Monthly recap PDF
          </button>
          <button onClick={() => downloadCsv(`/admin/invoices.csv${kind ? `?kind=${kind}` : ""}`, "cyannect-invoices.csv")} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="invoice-export-csv">
            <Download className="w-3 h-3" /> CSV
          </button>
        </div>
      </div>

      <div className="flex gap-2 mt-6 mb-4">
        {[["", "All"], ["subscription", "Subscriptions"], ["wallet_payout", "Wallet payouts"]].map(([v, label]) => (
          <button key={v} onClick={() => setKind(v)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${kind === v ? "pf-btn-primary" : "pf-btn-glass"}`} data-testid={`inv-filter-${v || "all"}`}>{label}</button>
        ))}
      </div>

      <div className="pf-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">Party</th>
              <th className="px-5 py-3">Amount</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Reference</th>
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3 text-right">Statement</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const to = i.kind === "subscription"
                ? (i.owner_email || "")
                : (i.user_email || "");
              return (
                <tr key={i.id} className="border-t border-white/60" data-testid={`invoice-${i.id}`}>
                  <td className="px-5 py-3">
                    <span className={`pf-chip ${i.kind === "subscription" ? "pf-chip-cyan" : "pf-chip-lav"}`}>
                      {i.kind === "subscription" ? "SaaS" : "Payout"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-800">
                    {i.kind === "subscription" ? (i.store_name || "—") : (i.user_email || "—")}
                  </td>
                  <td className="px-5 py-3 font-mono">{i.currency} ${(i.amount || 0).toFixed(2)}</td>
                  <td className="px-5 py-3"><span className={`pf-chip ${i.status === "paid" ? "pf-chip-mint" : i.status === "rejected" ? "" : "pf-chip-lav"}`}>{i.status}</span></td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-500">{(i.session_id || i.id || "").slice(0, 24)}…</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{new Date(i.created_at).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => downloadPdf(`/admin/invoices/${i.id}/pdf`, `cyannect-invoice-${i.id.slice(0, 12)}.pdf`)}
                        className="pf-btn-glass px-2.5 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1"
                        data-testid={`invoice-pdf-${i.id}`}
                        title="Download PDF statement"
                      >
                        <Download className="w-3 h-3" /> PDF
                      </button>
                      <button
                        onClick={() => setEmailModal({ invoiceId: i.id, defaultTo: to })}
                        className="pf-btn-glass px-2.5 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1"
                        data-testid={`invoice-email-${i.id}`}
                        title="Email PDF to the customer"
                      >
                        <Mail className="w-3 h-3" /> Email
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500">No invoices found for this filter.</td></tr>}
          </tbody>
        </table>
      </div>

      {emailModal && (
        <EmailInvoiceModal
          invoiceId={emailModal.invoiceId}
          defaultTo={emailModal.defaultTo}
          onClose={() => setEmailModal(null)}
        />
      )}

      {recapModal && (
        <MonthlyRecapModal
          stores={stores}
          defaultMonth={defaultMonth}
          onClose={() => setRecapModal(false)}
        />
      )}

      {batchModal && (
        <RecapBatchModal
          defaultMonth={defaultMonth}
          onClose={() => setBatchModal(false)}
        />
      )}
    </div>
  );
}


function EmailInvoiceModal({ invoiceId, defaultTo, onClose }) {
  const [to, setTo] = useState(defaultTo || "");
  const [ccAdmin, setCcAdmin] = useState(false);
  const [sending, setSending] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!to.trim()) { toast.error("Recipient email is required"); return; }
    setSending(true);
    try {
      await emailPdf(`/admin/invoices/${invoiceId}/email`, { to: to.trim(), cc_admin: ccAdmin });
      onClose();
    } finally { setSending(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="email-modal-root">
      <form onSubmit={submit} className="pf-card w-full max-w-md p-6 relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700" data-testid="email-modal-close">
          <X className="w-4 h-4" />
        </button>
        <div className="pf-chip pf-chip-cyan mb-2"><Mail className="w-3 h-3" /> Email statement</div>
        <h3 className="font-display text-xl font-medium text-slate-900">Send PDF invoice</h3>
        <p className="text-xs text-slate-500 mt-1">The Cyannect-branded PDF will be attached. Emails are logged to <code>email_log</code> until Resend keys are provided.</p>
        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-4 mb-1">Recipient</label>
        <input
          type="email"
          required
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="customer@example.com"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white/70 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm"
          data-testid="email-modal-to"
        />
        <label className="mt-3 flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={ccAdmin} onChange={(e) => setCcAdmin(e.target.checked)} data-testid="email-modal-cc" />
          Also send me a copy
        </label>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold">Cancel</button>
          <button type="submit" disabled={sending} className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold" data-testid="email-modal-send">
            {sending ? "Sending…" : "Send email"}
          </button>
        </div>
      </form>
    </div>
  );
}


function MonthlyRecapModal({ stores, defaultMonth, onClose }) {
  const [storeId, setStoreId] = useState(stores[0]?.id || "");
  const [month, setMonth] = useState(defaultMonth);
  const [to, setTo] = useState("");
  const [ccAdmin, setCcAdmin] = useState(false);
  const [busy, setBusy] = useState(false);

  const selected = stores.find((s) => s.id === storeId);
  const recipient = to.trim() || selected?.owner_email || "";

  async function handleDownload() {
    if (!storeId || !month) { toast.error("Pick a merchant and month"); return; }
    setBusy(true);
    try {
      await downloadPdf(`/admin/invoices-recap.pdf?store_id=${storeId}&month=${month}`,
        `cyannect-recap-${storeId.slice(0, 8)}-${month}.pdf`);
    } finally { setBusy(false); }
  }

  async function handleEmail() {
    if (!storeId || !month) { toast.error("Pick a merchant and month"); return; }
    if (!recipient) { toast.error("No recipient email available"); return; }
    setBusy(true);
    try {
      await emailPdf(`/admin/invoices-recap/email`, {
        store_id: storeId, month, to: to.trim() || undefined, cc_admin: ccAdmin,
      });
      onClose();
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="recap-modal-root">
      <div className="pf-card w-full max-w-lg p-6 relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700" data-testid="recap-modal-close">
          <X className="w-4 h-4" />
        </button>
        <div className="pf-chip pf-chip-cyan mb-2"><FileDown className="w-3 h-3" /> Monthly recap</div>
        <h3 className="font-display text-xl font-medium text-slate-900">Merchant monthly billing recap</h3>
        <p className="text-xs text-slate-500 mt-1">Aggregates subscription charges + verified conversion performance fees + wallet payouts for one merchant in the chosen month.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Merchant</label>
            <select
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white/70 text-sm"
              data-testid="recap-store-select"
            >
              {stores.length === 0 && <option value="">No merchants</option>}
              {stores.map((s) => (
                <option key={s.id} value={s.id}>{s.name || "(unnamed)"} — {s.owner_email || "—"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Month</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white/70 text-sm"
              data-testid="recap-month-input"
            />
          </div>
        </div>

        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3 mb-1">
          Recipient (optional — defaults to merchant email)
        </label>
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder={selected?.owner_email || "customer@example.com"}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white/70 text-sm"
          data-testid="recap-to-input"
        />
        <label className="mt-3 flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={ccAdmin} onChange={(e) => setCcAdmin(e.target.checked)} data-testid="recap-cc" />
          Also send me a copy
        </label>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={handleDownload} disabled={busy} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="recap-download">
            <Download className="w-3 h-3" /> Download PDF
          </button>
          <button type="button" onClick={handleEmail} disabled={busy} className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="recap-email">
            <Mail className="w-3 h-3" /> {busy ? "Sending…" : "Email PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}



function RecapBatchModal({ defaultMonth, onClose }) {
  const [month, setMonth] = useState(defaultMonth);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function submit() {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/invoices-recap/send-all", { month });
      setResult(data);
      toast.success(`Sent ${data.sent} recap${data.sent === 1 ? "" : "s"} · ${data.skipped} skipped`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Batch send failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="batch-modal-root">
      <div className="pf-card w-full max-w-lg p-6 relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700" data-testid="batch-modal-close">
          <X className="w-4 h-4" />
        </button>
        <div className="pf-chip pf-chip-cyan mb-2"><Mail className="w-3 h-3" /> Batch send</div>
        <h3 className="font-display text-xl font-medium text-slate-900">Email monthly recap to all merchants</h3>
        <p className="text-xs text-slate-500 mt-1">
          Auto-runs on the 1st of every month at 09:00 UTC. Trigger a manual run below to backfill or preview. Merchants with no owner email or zero billable activity are skipped.
        </p>
        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-4 mb-1">Recap month</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white/70 text-sm"
          data-testid="batch-month-input"
        />
        {result && (
          <div className="mt-4 grid grid-cols-4 gap-2 text-center" data-testid="batch-result">
            <div className="rounded-lg bg-slate-50 p-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Attempted</div>
              <div className="text-lg font-semibold text-slate-900">{result.attempted}</div>
            </div>
            <div className="rounded-lg bg-emerald-50 p-2">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700">Sent</div>
              <div className="text-lg font-semibold text-emerald-900">{result.sent}</div>
            </div>
            <div className="rounded-lg bg-amber-50 p-2">
              <div className="text-[10px] uppercase tracking-widest text-amber-700">Skipped</div>
              <div className="text-lg font-semibold text-amber-900">{result.skipped}</div>
            </div>
            <div className="rounded-lg bg-rose-50 p-2">
              <div className="text-[10px] uppercase tracking-widest text-rose-700">Failed</div>
              <div className="text-lg font-semibold text-rose-900">{result.failed}</div>
            </div>
          </div>
        )}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold">Close</button>
          <button type="button" onClick={submit} disabled={busy} className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="batch-send">
            <Mail className="w-3 h-3" /> {busy ? "Sending…" : "Send now"}
          </button>
        </div>
      </div>
    </div>
  );
}
